import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  BookOpenIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const HANDBOOK_URL = '/assets/documents/PSMS72M-Student-Handbook-2026-2027.pdf';
const HANDBOOK_FILE = 'PSMS72M-Student-Handbook-2026-2027.pdf';

const HandbookPage = () => {
  const sections = [
    {
      icon: UserGroupIcon,
      title: 'School Leadership Team',
      page: 'Page 3',
      items: ['Leadership contacts and emails', 'Mission and Vision Statements']
    },
    {
      icon: ClockIcon,
      title: 'Basic Operational Policies',
      page: 'Page 4',
      items: [
        'Arrival procedures and late arrival',
        'Dismissal procedures',
        'Attendance policy',
        'Bell schedule',
        'Breakfast and lunch',
        'Bathroom policy',
        'Lost and found'
      ]
    },
    {
      icon: AcademicCapIcon,
      title: 'Student Academic Responsibilities',
      page: 'Page 7',
      items: [
        'Grading policy and make-up work',
        'Online expectations',
        'Citizen and Student of the Month',
        'Student Council, PBIS, and Students’ Advisory'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Code of Conduct',
      page: 'Page 10',
      items: [
        'NYC DOE Discipline Code',
        'Dress code',
        'Rules, infractions, and consequences',
        'Student agreement',
        'Student supports and interventions'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
              <BookOpenIcon className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Student <span className="text-secondary">Handbook</span>
            </h1>
            <p className="text-xl text-white/90 font-light mb-3">
              2026&ndash;2027 School Year
            </p>
            <p className="text-white/70 font-light max-w-2xl mx-auto">
              Everything students and families need to know about our daily routines, academic
              expectations, and code of conduct at The Lexington Academy.
            </p>

            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <a
                href={HANDBOOK_URL}
                download={HANDBOOK_FILE}
                className="bg-orange-700 hover:bg-orange-800 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(244,101,36,0.3)] hover:shadow-[0_0_30px_rgba(244,101,36,0.5)] hover:-translate-y-1 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
                Download the Handbook (PDF)
              </a>
              <a
                href={HANDBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                Open in a New Tab
                <ArrowTopRightOnSquareIcon className="w-5 h-5" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>

            <p className="text-white/60 text-sm mt-6">PDF &middot; 17 pages &middot; 281 KB</p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            WHAT'S INSIDE
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-primary px-8 py-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <BookOpenIcon className="w-7 h-7 text-secondary" aria-hidden="true" />
                  What&rsquo;s Inside
                </h2>
              </div>

              <div className="p-8 grid md:grid-cols-2 gap-6">
                {sections.map((section) => (
                  <div
                    key={section.title}
                    className="p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:border-primary/20 transition-colors"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-white border border-gray-200 text-primary flex-shrink-0">
                        <section.icon className="w-6 h-6" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg leading-tight">{section.title}</h3>
                        <p className="text-sm text-orange-700 font-semibold mt-1">{section.page}</p>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-gray-700">
                      {section.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-secondary mt-1" aria-hidden="true">&bull;</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================================
            EMBEDDED READER
        ============================================================================ */}
        <section className="pb-16 md:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Read It Here</h2>
                <a
                  href={HANDBOOK_URL}
                  download={HANDBOOK_FILE}
                  className="text-sm font-bold text-orange-700 hover:text-orange-800 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
                  Download a copy
                </a>
              </div>

              <object
                data={HANDBOOK_URL}
                type="application/pdf"
                className="w-full h-[600px] md:h-[800px] bg-gray-100"
                aria-label="2026-2027 Student Handbook, embedded PDF reader"
              >
                <div className="p-8 text-center">
                  <p className="text-gray-700 mb-4">
                    Your browser can&rsquo;t display the handbook here. You can still read it by
                    downloading the PDF.
                  </p>
                  <a
                    href={HANDBOOK_URL}
                    download={HANDBOOK_FILE}
                    className="inline-flex items-center gap-2 bg-orange-700 hover:bg-orange-800 text-white px-6 py-3 rounded-full font-bold transition-colors"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
                    Download the Handbook (PDF)
                  </a>
                </div>
              </object>
            </div>

            {/* Help note */}
            <div className="mt-8 bg-primary text-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
              <div className="relative z-10">
                <h2 className="text-xl font-bold mb-2">Questions about the handbook?</h2>
                <p className="text-white/80 mb-6 max-w-2xl">
                  Our Parent Coordinator, Ms. Talyiah Nickens, is happy to walk you through any
                  section of the handbook or provide a printed copy.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="mailto:tnickens2@schools.nyc.gov"
                    className="bg-white text-primary px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
                  >
                    tnickens2@schools.nyc.gov
                  </a>
                  <a
                    href="/contact"
                    className="bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-full font-bold transition-colors"
                  >
                    Contact the School
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HandbookPage;
